import { useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { AUTH_BASE_URL } from "../config";
import { clearAppStorage } from "../services/storage";

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes of no user activity
const CHECK_INTERVAL_MS = 30 * 1000;    // evaluate idle every 30s
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click", "wheel"];

/**
 * Logs the user out of the Axaris session after 30 minutes of no real USER activity.
 *
 * Tracks user-interaction events (not background API calls), so the app's 5-minute polling does NOT
 * keep an abandoned-but-open session alive (which is why a server-side sliding window alone wouldn't
 * enforce an inactivity timeout here). On idle it clears the app cookie via `/auth/signout-local` —
 * the Entra SSO session is left intact, so a returning user re-logs in via a silent redirect rather
 * than a full sign-out. The 30-min server sliding window is the matching backstop.
 *
 * Must be used inside AuthProvider (e.g. PrivateRoute).
 */
export const useIdleLogout = () => {
  const { isAuthenticated } = useAuth();
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    lastActivityRef.current = Date.now();
    const markActive = () => { lastActivityRef.current = Date.now(); };

    const signOutIdle = () => {
      clearAppStorage();
      const returnUrl = `${window.location.origin}/login`;
      window.location.href = `${AUTH_BASE_URL}/auth/signout-local?returnUrl=${encodeURIComponent(returnUrl)}`;
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    // Catch "was away while the tab was backgrounded" on return — before any activity resets the timer.
    const onVisible = () => {
      if (document.visibilityState === "visible" &&
          Date.now() - lastActivityRef.current >= IDLE_TIMEOUT_MS) {
        signOutIdle();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    const interval = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= IDLE_TIMEOUT_MS) {
        signOutIdle();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [isAuthenticated]);
};
